import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FrontendLoggerService } from './frontend-logger.service';
import { ApiService } from './api.service';
import { PaginatedDocumentsResponse, DocumentUploadResponse } from '../models/documents/document.model';
import { ContentTree } from '../models/documents/folder.model';
import { FolderRequest } from '../models/documents/folder-request.model';
import { DocumentVersionInfo } from '../models/documents/document-version.model';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  constructor(private api: ApiService, private frontendLogger: FrontendLoggerService) {}

  /**
   * Sube un documento (POST /documents/upload)
   * Puede devolver status: created, queued, duplicated
   */
  uploadDocument(
    file: File, 
    folderId: number,
    processImmediately?: boolean,
    folderName?: string,
    tags?: string[],
    roleId?: string
  ): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderId', folderId.toString());
    
    if (processImmediately !== undefined) {
      formData.append('processImmediately', processImmediately.toString());
    }
    
    if (folderName) {
      formData.append('folderName', folderName);
    }
    
    if (tags && tags.length > 0) {
      formData.append('tags', JSON.stringify(tags));
    }
    
    if (roleId) {
      formData.append('roleId', roleId);
    }
    
    this.frontendLogger.info('Subiendo documento para indexación', 'DocumentService.uploadDocument');
    return this.api.post<DocumentUploadResponse>(`documents/upload`, formData);
  }

  /**
   * Obtiene documentos con búsqueda y paginación (GET /documents)
   */
  getDocuments(searchTerm?: string, page: number = 1, pageSize: number = 10): Observable<PaginatedDocumentsResponse> {
    let params: any = { page, pageSize };
    if (searchTerm) params.searchTerm = searchTerm;
    this.frontendLogger.info('Solicitando lista de documentos indexados', 'DocumentService.getDocuments');
    return this.api.get<PaginatedDocumentsResponse>(`documents/get-documents`, params);
  }

  /**
   * Reemplaza un documento existente (POST /documents/replace)
   * Puede devolver status: created, queued
   */
  replaceDocument(file: File, original_filename: string, roleId?: string): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('original_filename', original_filename);
    
    if (roleId) {
      formData.append('roleId', roleId);
    }
    
    this.frontendLogger.info('Reemplazando documento', 'DocumentService.replaceDocument');
    return this.api.post<DocumentUploadResponse>(`documents/replace`, formData);
  }

  /**
   * Elimina un documento por ID (DELETE /documents/{documentId})
   */
  deleteDocument(documentId: string): Observable<any> {
    this.frontendLogger.info(`Eliminando documento ${documentId}`, 'DocumentService.deleteDocument');
    return this.api.delete<any>(`documents/${documentId}`);
  }

  /**
   * R3.5 C10: historial de versiones de un documento (GET /documents/{id}/versions).
   * 404 si la feature doc_versioning no está en el plan (el recurso queda oculto).
   */
  getDocumentVersions(documentId: string): Observable<DocumentVersionInfo[]> {
    this.frontendLogger.info(`Solicitando historial de versiones de ${documentId}`, 'DocumentService.getDocumentVersions');
    return this.api.get<DocumentVersionInfo[]>(`documents/${documentId}/versions`);
  }

  /**
   * Obtiene todas las carpetas (GET /folders/get-folder-structure)
   */
  getFolderTreeStructure(): Observable<ContentTree> {
    this.frontendLogger.info('Solicitando carpetas de documentos', 'DocumentService.getFolderTreeStructure');
    return this.api.get<ContentTree>(`document-folder/get-folder-structure`);
  }

  /**
   * Crea una carpeta (POST /folders)
   */
  createFolder(data: FolderRequest): Observable<void> {
    this.frontendLogger.info('Creando carpeta de documentos', 'DocumentService.createFolder');
    return this.api.post<void>(`document-folder`, data);
  }

  /**
   * Actualiza un contenido (carpeta o archivo) por ID de contenido (PUT /document-folder/{id})
   * Usado para mover archivos cambiando ParentId y manteniendo metadatos.
   */
  updateDocumentFolder(contentId: number, data: any): Observable<any> {
    this.frontendLogger.info(`Actualizando contenido ${contentId} (document-folder)`, 'DocumentService.updateDocumentFolder');
    return this.api.put<any>(`document-folder/${contentId}`, data);
  }

  /**
   * Obtiene estadísticas de documentos por tipo (para donut/pie chart)
   */
  getDocumentStats(): Observable<{ type: string; count: number }[]> {
    this.frontendLogger.info('Obteniendo estadísticas de documentos por tipo', 'DocumentService.getDocumentStats');
    return this.api.get<{ type: string; count: number }[]>(`documents/stats`);
  }

  /**
   * Obtiene documentos agrupados por fecha (para vista árbol/expansible)
   */
  getGroupedDocumentsByDate(): Observable<any[]> {
    this.frontendLogger.info('Obteniendo documentos agrupados por fecha', 'DocumentService.getGroupedDocumentsByDate');
    return this.api.get<any[]>(`documents/grouped-by-date`);
  }

  /**
   * Obtiene el mapa de dominios para visualización tipo red
   */
  getDocumentDomainMap(): Observable<{ domains: string[], nodes: any[], links: any[] }> {
    this.frontendLogger.info('Obteniendo mapa de dominios de documentos', 'DocumentService.getDocumentDomainMap');
    return this.api.get<{ domains: string[], nodes: any[], links: any[] }>(`documents/domain-map`);
  }

  /**
   * Obtiene la URL pública (o firmada) para previsualizar/descargar un documento por su documentId.
   * Este endpoint devuelve texto plano (la URL directa), no JSON.
   */
  getDocumentPublicUrl(documentId: string): Observable<string> {
    this.frontendLogger.info(`Obteniendo publicUrl para documento ${documentId}`, 'DocumentService.getDocumentPublicUrl');
    var result = this.api.getText(`documents/public-url`, { documentId });
    return result;
  }

  /**
   * Descarga el contenido binario del documento vía backend (evita CORS en el navegador).
   */
  getDocumentContentBlob(documentId: string): Observable<Blob> {
    this.frontendLogger.info(`Descargando contenido (blob) para documento ${documentId}`, 'DocumentService.getDocumentContentBlob');
    var result = this.api.getBlob(`documents/content`, { documentId });
    return result;
  }

  /**
   * Descarga el contenido como texto (para txt/csv) vía backend.
   */
  getDocumentContentText(documentId: string): Observable<string> {
    this.frontendLogger.info(`Descargando contenido (texto) para documento ${documentId}`, 'DocumentService.getDocumentContentText');
    var result = this.api.getText(`documents/content`, { documentId });
    return result;
  }
}
